import { useState, useEffect, useRef, useCallback } from 'react'
import { Send, Upload, Image, Trash2, Bot, User, RefreshCw, Loader2, Brain, Wrench, ChevronDown, ChevronUp, AlertCircle, FileText } from 'lucide-react'
import { auth } from '@/firebase'
import { API_BASE_URL } from '@/config'
import { useTranslation } from '@/i18n'
import MarkdownMessage from '@/components/MarkdownMessage'

// ─── Types ──────────────────────────────────────────

interface ToolCall {
  id: string
  name: string
  status: 'running' | 'done' | 'error'
}

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  streaming?: boolean
  thinking?: boolean
  thinkingText?: string
  toolCalls?: ToolCall[]
  fileName?: string
  hasImage?: boolean
}

interface PendingRequest {
  resolve: (data: unknown) => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
}

// ─── Constants ──────────────────────────────────────

const REQUEST_TIMEOUT = 30000
const KEEPALIVE_INTERVAL = 30000
const RECONNECT_BASE_DELAY = 1000
const RECONNECT_MAX_DELAY = 30000
const RECONNECT_MAX_ATTEMPTS = 10

function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

// ─── Content extraction helpers ─────────────────────

interface ContentBlock { type: string; text?: string; thinking?: string; id?: string; name?: string }

function extractText(content: unknown): string {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) return (content as ContentBlock[]).filter(c => c.type === 'text').map(c => c.text || '').join('')
  if (content && typeof content === 'object' && 'text' in content) return (content as { text: string }).text
  return ''
}

function extractThinking(content: unknown): string {
  if (!Array.isArray(content)) return ''
  return (content as ContentBlock[]).filter(c => c.type === 'thinking').map(c => c.thinking || c.text || '').join('')
}

function extractToolCalls(content: unknown): ToolCall[] {
  if (!Array.isArray(content)) return []
  return (content as ContentBlock[]).filter(c => c.type === 'tool_use').map(c => ({
    id: c.id || generateId(), name: c.name || 'unknown', status: 'running' as const
  }))
}

// ─── Component ──────────────────────────────────────

export default function AgentTraining() {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedThinking, setExpandedThinking] = useState<Set<string>>(new Set())
  const [pendingImage, setPendingImage] = useState<string | null>(null)
  const [pendingFile, setPendingFile] = useState<{ name: string; content: string } | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const keepaliveRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pendingRequests = useRef<Map<string, PendingRequest>>(new Map())
  const mountedRef = useRef(true)
  const intentionalCloseRef = useRef(false)
  const reconnectAttemptRef = useRef(0)
  const gatewayInfoRef = useRef<{ url: string; token: string } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  // ─── WebSocket helpers ──────────────────────────────

  const rejectAllPending = useCallback((reason: string) => {
    pendingRequests.current.forEach(req => { clearTimeout(req.timer); req.reject(new Error(reason)) })
    pendingRequests.current.clear()
  }, [])

  const stopKeepalive = useCallback(() => {
    if (keepaliveRef.current) { clearInterval(keepaliveRef.current); keepaliveRef.current = null }
  }, [])

  const startKeepalive = useCallback(() => {
    stopKeepalive()
    keepaliveRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try { wsRef.current.send(JSON.stringify({ type: 'ping' })) } catch { stopKeepalive() }
      }
    }, KEEPALIVE_INTERVAL)
  }, [stopKeepalive])

  const sendRequest = useCallback((method: string, params: Record<string, unknown> = {}): Promise<unknown> => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) { reject(new Error('Not connected')); return }
      const id = generateId()
      const timer = setTimeout(() => {
        pendingRequests.current.delete(id)
        reject(new Error('Request timeout'))
      }, REQUEST_TIMEOUT)
      pendingRequests.current.set(id, { resolve, reject, timer })
      wsRef.current.send(JSON.stringify({ type: 'req', id, method, params }))
    })
  }, [])

  // ─── Event handler ──────────────────────────────────

  const handleWsMessage = useCallback((data: any) => {
    if (data.type === 'pong') return

    // Response to a pending request
    if (data.type === 'res' && data.id) {
      const req = pendingRequests.current.get(data.id)
      if (req) {
        clearTimeout(req.timer)
        pendingRequests.current.delete(data.id)
        if (data.ok === false) req.reject(new Error(data.error || 'Request failed'))
        else req.resolve(data.payload ?? data.result)
      }
      return
    }

    // Chat event stream
    if ((data.type === 'event' || data.type === 'evt') && data.event === 'chat') {
      const msg = data.payload || data.data
      if (!msg) return

      const msgId = msg.runId || msg.id || generateId()
      const msgRole = msg.message?.role || msg.role || 'assistant'
      const rawContent = msg.message?.content || msg.content
      const text = extractText(rawContent)
      const thinkingText = extractThinking(rawContent)
      const toolCalls = extractToolCalls(rawContent)
      const state = msg.state

      if (state === 'thinking') {
        setMessages(prev => {
          const existing = prev.find(m => m.id === msgId)
          if (existing) return prev.map(m => m.id === msgId ? { ...m, thinking: true, thinkingText: text || thinkingText || m.thinkingText } : m)
          return [...prev, { id: msgId, role: msgRole, content: '', timestamp: new Date(), thinking: true, thinkingText: text || thinkingText }]
        })
      } else if (state === 'tool_use' || state === 'tool_call') {
        const newTools = toolCalls.length > 0 ? toolCalls : [{ id: generateId(), name: msg.toolName || msg.tool || 'tool', status: 'running' as const }]
        setMessages(prev => {
          const existing = prev.find(m => m.id === msgId)
          if (existing) return prev.map(m => m.id === msgId ? { ...m, thinking: false, toolCalls: [...(m.toolCalls || []), ...newTools] } : m)
          return [...prev, { id: msgId, role: msgRole, content: '', timestamp: new Date(), toolCalls: newTools }]
        })
      } else if (state === 'tool_result') {
        const toolId = msg.toolCallId || msg.toolId
        setMessages(prev => prev.map(m => {
          if (m.id === msgId && m.toolCalls) return { ...m, toolCalls: m.toolCalls.map(tc => (tc.id === toolId || !toolId) ? { ...tc, status: 'done' as const } : tc) }
          return m
        }))
      } else if (state === 'delta') {
        setMessages(prev => {
          const existing = prev.find(m => m.id === msgId)
          if (existing) return prev.map(m => m.id === msgId ? { ...m, content: text, streaming: true, thinking: false, thinkingText: thinkingText || m.thinkingText } : m)
          return [...prev, { id: msgId, role: msgRole, content: text, timestamp: new Date(), streaming: true, thinkingText }]
        })
      } else if (state === 'done') {
        setMessages(prev => prev.map(m => m.id === msgId ? {
          ...m, content: text || m.content, streaming: false, thinking: false,
          thinkingText: thinkingText || m.thinkingText,
          toolCalls: m.toolCalls?.map(tc => ({ ...tc, status: 'done' as const })),
        } : m))
        setIsSending(false)
      } else if (state === 'error') {
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, streaming: false, thinking: false } : m))
        setIsSending(false)
        setError(text || 'Agent encountered an error')
      } else if (text) {
        setMessages(prev => {
          if (prev.some(m => m.id === msgId)) return prev.map(m => m.id === msgId ? { ...m, content: text, streaming: false } : m)
          return [...prev, { id: msgId, role: msgRole, content: text, timestamp: new Date() }]
        })
        setIsSending(false)
      }
    }
  }, [])

  // ─── Connect ──────────────────────────────────────

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return
    setIsConnecting(true)
    setError(null)
    intentionalCloseRef.current = false

    try {
      // Get gateway info from server if not cached
      if (!gatewayInfoRef.current) {
        const token = await auth.currentUser?.getIdToken()
        if (!token) { setError('Not authenticated'); setIsConnecting(false); return }
        const res = await fetch(`${API_BASE_URL}/training/gateway`, { headers: { Authorization: `Bearer ${token}` } })
        if (!res.ok) { setError('Failed to get gateway info'); setIsConnecting(false); return }
        const data = await res.json()
        gatewayInfoRef.current = { url: data.gatewayUrl, token: data.gatewayToken }
      }

      const { url: gwUrl, token: gwToken } = gatewayInfoRef.current
      const wsUrl = new URL(gwUrl.replace(/^http/, 'ws'))
      if (gwToken) wsUrl.searchParams.set('token', gwToken)

      const ws = new WebSocket(wsUrl.toString())
      wsRef.current = ws

      ws.onopen = async () => {
        if (!mountedRef.current) return
        try {
          await sendRequest('connect', {
            minProtocol: 3, maxProtocol: 3,
            client: { id: 'webchat-ui', displayName: 'ClawCustomer Agent Training', version: '2.0.0', platform: 'web', mode: 'webchat' },
            caps: [],
            auth: gwToken ? { token: gwToken } : undefined,
          })
          if (!mountedRef.current) return
          setIsConnected(true)
          setIsConnecting(false)
          setError(null)
          reconnectAttemptRef.current = 0
          startKeepalive()

          // Load chat history
          try {
            const result = await sendRequest('chat.history', { sessionKey: 'main', limit: 50 }) as any
            const msgs = result?.messages || result?.payload?.messages
            if (msgs && mountedRef.current) {
              setMessages(msgs.map((m: any) => ({
                id: m.id || generateId(),
                role: m.role,
                content: extractText(m.content),
                timestamp: new Date(m.timestamp || Date.now()),
                thinkingText: extractThinking(m.content),
              })))
            }
          } catch { /* no history yet */ }
        } catch (e) {
          if (!mountedRef.current) return
          setError(e instanceof Error ? e.message : 'Connection handshake failed')
          setIsConnecting(false)
        }
      }

      ws.onmessage = (event) => {
        try { handleWsMessage(JSON.parse(event.data)) } catch { /* parse error */ }
      }

      ws.onerror = () => console.error('[TUI] WebSocket error')

      ws.onclose = (event) => {
        if (!mountedRef.current) return
        stopKeepalive()
        setIsConnected(false)
        setIsConnecting(false)
        wsRef.current = null
        rejectAllPending('Connection closed')
        setIsSending(false)

        if (!intentionalCloseRef.current && event.code !== 1000) {
          const attempt = reconnectAttemptRef.current
          if (attempt < RECONNECT_MAX_ATTEMPTS) {
            const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, attempt), RECONNECT_MAX_DELAY)
            reconnectAttemptRef.current = attempt + 1
            setError(`Disconnected. Reconnecting in ${Math.round(delay / 1000)}s...`)
            reconnectTimeoutRef.current = setTimeout(() => { if (mountedRef.current) connect() }, delay)
          } else {
            setError('Connection lost. Click reconnect to try again.')
          }
        }
      }
    } catch {
      setError('Failed to connect')
      setIsConnecting(false)
    }
  }, [sendRequest, handleWsMessage, rejectAllPending, startKeepalive, stopKeepalive])

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true
    if (reconnectTimeoutRef.current) { clearTimeout(reconnectTimeoutRef.current); reconnectTimeoutRef.current = null }
    stopKeepalive()
    rejectAllPending('Disconnected')
    if (wsRef.current) { wsRef.current.close(1000); wsRef.current = null }
    setIsConnected(false)
    setIsSending(false)
    reconnectAttemptRef.current = 0
  }, [stopKeepalive, rejectAllPending])

  const manualReconnect = useCallback(() => {
    disconnect()
    gatewayInfoRef.current = null // re-fetch gateway info
    reconnectAttemptRef.current = 0
    setTimeout(() => { if (mountedRef.current) connect() }, 100)
  }, [disconnect, connect])

  useEffect(() => {
    mountedRef.current = true
    connect()
    return () => { mountedRef.current = false; disconnect() }
  }, [])

  // ─── Send message ──────────────────────────────────

  const handleSend = async () => {
    let content = input.trim()
    if (!content && !pendingImage && !pendingFile) return
    if (!isConnected || isSending) return

    // Build message content
    if (pendingFile) {
      content = `[File: ${pendingFile.name}]\n\`\`\`\n${pendingFile.content}\n\`\`\`\n\n${content || '请分析这个文件'}`
    }

    const userMsg: Message = {
      id: generateId(), role: 'user', content, timestamp: new Date(),
      fileName: pendingFile?.name, hasImage: !!pendingImage,
    }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setPendingImage(null)
    setPendingFile(null)
    setIsSending(true)

    try {
      // Send via REST API (webchat-ui lacks operator.write scope for WS chat.send)
      // WebSocket is used for receiving real-time events (thinking/tool/done)
      const token = await auth.currentUser?.getIdToken()
      if (!token) { setIsSending(false); return }

      const body: any = { message: content }
      if (pendingImage) body.imageData = pendingImage

      const res = await fetch(`${API_BASE_URL}/training/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      })

      if (res.ok) {
        const data = await res.json()
        if (data.response) {
          // Add response if WS events didn't already deliver it
          setMessages(prev => {
            if (prev.some(m => m.role === 'assistant' && m.content === data.response)) return prev
            return [...prev, { id: generateId(), role: 'assistant' as const, content: data.response, timestamp: new Date() }]
          })
        }
      } else {
        const errData = await res.json().catch(() => ({}))
        setError(errData.message || 'Agent response failed')
      }
      setIsSending(false)
    } catch {
      setError('Failed to send message')
      setIsSending(false)
    }
  }

  // ─── File/Image handlers ───────────────────────────

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) return
    const reader = new FileReader()
    reader.onload = () => setPendingImage((reader.result as string).split(',')[1] || '')
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || file.size > 5 * 1024 * 1024) return
    const reader = new FileReader()
    reader.onload = () => setPendingFile({ name: file.name, content: reader.result as string })
    reader.readAsText(file)
    e.target.value = ''
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  function toggleThinking(msgId: string) {
    setExpandedThinking(prev => {
      const next = new Set(prev)
      if (next.has(msgId)) next.delete(msgId); else next.add(msgId)
      return next
    })
  }

  // ─── Render ────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('nav.knowledgeBase')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('common.loading') === 'Loading...'
              ? 'Chat with your AI Agent — teach knowledge, configure tools, correct mistakes.'
              : '与你的 AI Agent 聊天 — 教授知识、配置工具、纠正错误。'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : isConnecting ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'}`} />
            <span className="text-xs text-gray-500">
              {isConnecting ? 'Connecting...' : isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          {!isConnected && !isConnecting && (
            <button onClick={manualReconnect} className="p-1.5 hover:bg-gray-100 rounded-md" title="Reconnect">
              <RefreshCw className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {error && (
          <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {messages.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 space-y-3">
            <Bot className="h-12 w-12" />
            <p className="text-center">
              {t('common.loading') === 'Loading...'
                ? 'Start training your Agent. Teach it about your business.'
                : '开始训练你的 Agent，教它了解你的业务。'}
            </p>
          </div>
        )}

        <div className="space-y-4">
          {messages.map(msg => {
            const isUser = msg.role === 'user'
            const hasThinking = !!msg.thinkingText
            const isThinkingExpanded = expandedThinking.has(msg.id)

            return (
              <div key={msg.id} className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1 ${
                  isUser ? 'bg-indigo-600' : 'bg-emerald-600'
                }`}>
                  {isUser ? <User className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4 text-white" />}
                </div>

                <div className="max-w-[75%] space-y-1">
                  {/* Thinking indicator */}
                  {msg.thinking && !msg.content && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg text-purple-600 text-sm">
                      <Brain className="h-4 w-4" />
                      <span>Thinking</span>
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}

                  {/* Thinking toggle */}
                  {hasThinking && !msg.thinking && (
                    <button onClick={() => toggleThinking(msg.id)} className="flex items-center gap-1.5 text-purple-500 text-xs hover:text-purple-700 py-0.5">
                      <Brain className="h-3 w-3" />
                      <span>{isThinkingExpanded ? 'Hide thinking' : 'Show thinking'}</span>
                      {isThinkingExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  )}
                  {hasThinking && isThinkingExpanded && (
                    <div className="bg-purple-50 border-l-2 border-purple-300 pl-3 pr-3 py-2 rounded-r text-gray-500 text-sm italic">
                      {msg.thinkingText}
                    </div>
                  )}

                  {/* Tool calls */}
                  {msg.toolCalls && msg.toolCalls.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {msg.toolCalls.map(tool => (
                        <div key={tool.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          tool.status === 'running' ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500'
                        }`}>
                          {tool.status === 'running' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />}
                          {tool.name}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* File/image badge */}
                  {msg.fileName && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                      <FileText className="h-3 w-3" /> {msg.fileName}
                    </div>
                  )}

                  {/* Message content */}
                  {(msg.content || msg.streaming) && (
                    <div className={`rounded-lg px-3 py-2 ${
                      isUser ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-100'
                    }`}>
                      <MarkdownMessage content={msg.content} className={isUser ? 'text-gray-800' : 'text-gray-700'} />
                      {msg.streaming && <span className="inline-block w-1.5 h-4 bg-emerald-500 animate-pulse ml-0.5 align-middle rounded-sm" />}
                    </div>
                  )}

                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            )
          })}

          {isSending && !messages.some(m => m.streaming || m.thinking) && (
            <div className="flex items-center gap-2 text-gray-400 text-sm pl-11">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Agent is responding...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Attachments preview */}
      {(pendingImage || pendingFile) && (
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 border-b-0 rounded-t-lg mt-2">
          {pendingImage && (
            <div className="relative">
              <img src={`data:image/jpeg;base64,${pendingImage}`} alt="Preview" className="h-12 w-12 object-cover rounded" />
              <button onClick={() => setPendingImage(null)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">x</button>
            </div>
          )}
          {pendingFile && (
            <div className="flex items-center gap-1 bg-white px-2 py-1 rounded border text-sm">
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="text-gray-700 max-w-[200px] truncate">{pendingFile.name}</span>
              <button onClick={() => setPendingFile(null)} className="text-red-500 ml-1">x</button>
            </div>
          )}
        </div>
      )}

      {/* Input area */}
      <div className={`bg-white border border-gray-200 shadow-sm ${pendingImage || pendingFile ? 'rounded-b-lg' : 'rounded-lg mt-2'}`}>
        <div className="flex items-end p-2 gap-2">
          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
          <input ref={fileInputRef} type="file" accept=".md,.txt,.csv,.json,.xml,.html" className="hidden" onChange={handleFileSelect} />

          <button onClick={() => imageInputRef.current?.click()} className="p-2 text-gray-400 hover:text-indigo-600 transition-colors" title="Upload image" type="button">
            <Image className="h-5 w-5" />
          </button>
          <button onClick={() => fileInputRef.current?.click()} className="p-2 text-gray-400 hover:text-indigo-600 transition-colors" title="Upload file" type="button">
            <Upload className="h-5 w-5" />
          </button>

          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isConnected
              ? (t('common.loading') === 'Loading...' ? 'Train your Agent... (Enter to send)' : '训练你的Agent... (Enter 发送)')
              : 'Connecting...'}
            rows={1}
            className="flex-1 resize-none border-0 focus:ring-0 text-sm py-2 max-h-32 overflow-y-auto"
            disabled={!isConnected || isSending}
            style={{ minHeight: '36px' }}
          />

          <button
            onClick={handleSend}
            disabled={!isConnected || isSending || (!input.trim() && !pendingImage && !pendingFile)}
            className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            type="button"
          >
            {isSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </div>
  )
}
